import {JsonToKotlinSampleData} from '../../../../../DummyData/ConvertSampleJson';

import React, { useState, ChangeEvent } from 'react';

interface NestedClassMap {
  [key: string]: any;
}

const JsonToKotlin: React.FC = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the Kotlin output
  const [kotlinOutput, setKotlinOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for package name
  const [packageName, setPackageName] = useState<string>('com.example.model');
  // State for root class name
  const [rootClassName, setRootClassName] = useState<string>('RootClass');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isKotlinGenerated, setIsKotlinGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for nullability option
  const [makeNullable, setMakeNullable] = useState<boolean>(true);
  // State for serialization option
  const [addSerialization, setAddSerialization] = useState<boolean>(true);

  // Handle JSON input change
  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setJsonInput(e.target.value);
    setError('');
    setIsKotlinGenerated(false);
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Use example data
  const useExampleData = (): void => {
    const exampleJson = {
      "id": 1,
      "name": "John Doe",
      "email": "john.doe@example.com",
      "age": 30,
      "isActive": true,
      "scores": [85, 90, 78],
      "address": {
        "street": "123 Main St",
        "city": "New York",
        "zipCode": "10001"
      },
      "tags": ["developer", "kotlin"],
      "projects": [
        {
          "id": 101,
          "title": "Mobile App",
          "completed": false
        },
        {
          "id": 102,
          "title": "Web Portal",
          "completed": true
        }
      ]
    };
    setJsonInput(JSON.stringify(exampleJson, null, 2));
    setError('');
    setIsKotlinGenerated(false);
    setKotlinOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = (): void => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Clear all inputs and reset states
  const clearAll = (): void => {
    setJsonInput('');
    setKotlinOutput('');
    setError('');
    setRootClassName('RootClass');
    setPackageName('com.example.model');
    setIsKotlinGenerated(false);
  };

  // Convert primitive types to Kotlin types
  const getKotlinType = (value: any, makeNullable: boolean): string => {
    if (value === null) return 'Any' + (makeNullable ? '?' : '');
    
    const type = typeof value;
    
    switch (type) {
      case 'string':
        return 'String' + (makeNullable ? '?' : '');
      case 'number':
        return (Number.isInteger(value) ? 'Int' : 'Double') + (makeNullable ? '?' : '');
      case 'boolean':
        return 'Boolean' + (makeNullable ? '?' : '');
      case 'object':
        if (Array.isArray(value)) {
          if (value.length === 0) {
            return 'List<Any>' + (makeNullable ? '?' : '');
          }
          const firstItem = value[0];
          if (typeof firstItem === 'object' && firstItem !== null) {
            return `List<${capitalize(guessClassName(firstItem))}>` + (makeNullable ? '?' : '');
          } else {
            return `List<${getKotlinType(firstItem, false)}>` + (makeNullable ? '?' : '');
          }
        }
        return capitalize(guessClassName(value)) + (makeNullable ? '?' : '');
      default:
        return 'Any' + (makeNullable ? '?' : '');
    }
  };

  // Capitalize first letter of a string
  const capitalize = (str: string): string => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Camel case to Title case
  const toTitleCase = (str: string): string => {
    if (!str) return '';
    return str.replace(/([A-Z])/g, ' $1').replace(/^./, (s: string) => s.toUpperCase()).trim();
  };

  // Get a class name from an object key or fallback
  const guessClassName = (obj: any): string => {
    if (!obj || typeof obj !== 'object') return 'Object';
    
    // Try to find a good name from known fields
    const nameKeys = ['type', 'kind', 'class', 'name', 'title', 'id'];
    for (const key of nameKeys) {
      if (obj[key] && typeof obj[key] === 'string') {
        return capitalize(obj[key]);
      }
    }
    
    return 'Object';
  };

  // Convert a property name to Kotlin property name format
  const formatPropertyName = (name: string): string => {
    if (!name) return '';
    
    // Handle reserved keywords
    const reservedWords = ['in', 'is', 'as', 'by', 'if', 'do', 'for', 'fun', 'val', 'var'];
    if (reservedWords.includes(name)) {
      return `\`${name}\``;
    }
    
    return name;
  };

  // Generate Kotlin data class for an object
  const generateDataClass = (
    obj: any, 
    className: string, 
    indent: string = '', 
    nestedClasses: Map<string, any> = new Map(), 
    makeNullable: boolean, 
    addSerialization: boolean
  ): { code: string; nestedClasses: Map<string, any> } => {
    if (!obj || typeof obj !== 'object') return { code: '', nestedClasses };
    
    // Define serializable annotation
    const serializable = addSerialization ? '@Serializable\n' : '';
    
    let code = `${indent}${serializable}${indent}data class ${className}(\n`;
    
    // Get all properties
    const properties = Object.entries(obj);
    
    // Generate properties with types
    properties.forEach(([key, value], index) => {
      const isLast = index === properties.length - 1;
      const propertyName = formatPropertyName(key);
      
      // Handle nested objects
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const nestedClassName = `${className}${capitalize(key)}`;
        nestedClasses.set(nestedClassName, value);
        code += `${indent}    @SerialName("${key}")\n${indent}    val ${propertyName}: ${nestedClassName}${makeNullable ? '?' : ''}${isLast ? '' : ','}\n`;
      } 
      // Handle arrays with objects
      else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        const nestedClassName = `${className}${capitalize(key).replace(/s$/, '')}`;
        nestedClasses.set(nestedClassName, value[0]);
        code += `${indent}    @SerialName("${key}")\n${indent}    val ${propertyName}: List<${nestedClassName}>${makeNullable ? '?' : ''}${isLast ? '' : ','}\n`;
      }
      // Handle primitive types
      else {
        code += `${indent}    @SerialName("${key}")\n${indent}    val ${propertyName}: ${getKotlinType(value, makeNullable)}${isLast ? '' : ','}\n`;
      }
    });
    
    code += `${indent})\n`;
    
    return { code, nestedClasses };
  };

  // Convert JSON to Kotlin
  const convertJsonToKotlin = () => {
    setError('');
    setIsLoading(true);
    setIsKotlinGenerated(false);
    setKotlinOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      // Generate main class and collect nested classes
      const { code, nestedClasses } = generateDataClass(
        jsonData, 
        rootClassName, 
        '', 
        new Map(), 
        makeNullable, 
        addSerialization
      );
      
      // Generate all nested classes
      let nestedClassesCode = '';
      nestedClasses.forEach((value, className) => {
        const result = generateDataClass(
          value, 
          className, 
          '', 
          nestedClasses, 
          makeNullable, 
          addSerialization
        );
        nestedClassesCode += result.code + '\n';
      });
      
      // Compose the full Kotlin code
      const imports = addSerialization 
        ? 'import kotlinx.serialization.Serializable\nimport kotlinx.serialization.SerialName\n\n'
        : '';
      
      const fullKotlin = `package ${packageName}\n\n${imports}${nestedClassesCode}${code}`;
      
      setKotlinOutput(fullKotlin);
      setIsKotlinGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating Kotlin: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy Kotlin to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(kotlinOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download Kotlin file
  const downloadKotlin = () => {
    const blob = new Blob([kotlinOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${rootClassName}.kt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to Kotlin Data Class Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to Kotlin data classes. Paste your JSON data below to generate data classes with proper types and nested structures.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="rootClassName" className="block text-sm font-medium text-gray-700 mb-1">
                  Root Class Name
                </label>
                <input
                  type="text"
                  id="rootClassName"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={rootClassName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setRootClassName(e.target.value)}
                  placeholder="RootClass"
                />
              </div>
              
              <div>
                <label htmlFor="packageName" className="block text-sm font-medium text-gray-700 mb-1">
                  Package Name
                </label>
                <input
                  type="text"
                  id="packageName"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={packageName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setPackageName(e.target.value)}
                  placeholder="com.example.model"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="makeNullable"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={makeNullable}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setMakeNullable(e.target.checked)}
                  />
                  <label htmlFor="makeNullable" className="ml-2 block text-sm text-gray-700">
                    Make properties nullable
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="addSerialization"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={addSerialization}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setAddSerialization(e.target.checked)}
                  />
                  <label htmlFor="addSerialization" className="ml-2 block text-sm text-gray-700">
                    Add kotlinx.serialization annotations
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToKotlin}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Kotlin'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Kotlin Output
            </label>
            
            {isKotlinGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Kotlin'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadKotlin}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download .kt
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isKotlinGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    Kotlin data classes will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isKotlinGenerated && (
              <div className="flex-grow flex flex-col">
                <div className="flex-grow flex flex-col">
                  <label htmlFor="kotlinOutput" className="text-sm font-medium text-gray-700 mb-1">
                    Kotlin Code:
                  </label>
                  <textarea
                    id="kotlinOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={kotlinOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isKotlinGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">Kotlin Data Classes Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated code includes data classes with proper types, nested objects, and serialization annotations for direct use in your Kotlin project.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to Kotlin Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your JSON data into Kotlin data classes. It automatically detects types, creates nested classes for complex objects, and adds proper serialization annotations.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Type Inference</strong> - Automatically determines appropriate Kotlin types from JSON values</li>
              <li><strong>Nested Class Generation</strong> - Creates separate data classes for nested objects</li>
              <li><strong>Collection Support</strong> - Properly handles arrays and lists with correct generic types</li>
              <li><strong>Serialization Support</strong> - Adds kotlinx.serialization annotations for easy JSON parsing</li>
              <li><strong>Nullable Properties</strong> - Option to make properties nullable or non-nullable</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>Choose meaningful names for the root class to generate more readable nested class names</li>
              <li>Make sure your JSON represents the complete structure with all fields that you need in your classes</li>
              <li>For production code, you may need to review and adjust the generated classes for specific requirements</li>
              <li>If using Kotlin serialization, make sure to add the required dependencies to your project</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToKotlin;